import moment, { Moment } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { Allocation, ResourceType } from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { isDateBetweenRange } from 'components/organisms/resource-management/utils/dates';
import {
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
  resourceIdForUserAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { floatUid } from 'utils/uid';
import { numberOfWorkingDaysBetweenDates } from 'utils/working-days';
import { AllocationEvent, SchedulerEventType } from '../../event-types';
import { ExcludeNonWorkingDaysParam, GroupBy } from '../../types';
import { normalizeAllocationPreviewHtml } from '../preview-text';
import {
  getSchedulerEventProps,
  getSchedulerEventsClass,
} from './scheduler-event-styling';

export {
  getSchedulerEventProps,
  getSchedulerEventsClass,
} from './scheduler-event-styling';

export const PHASE_STYLE = {
  defaultTop: 8,
  defaultHeight: 24,
  totalAvailableSpace: 32,
};

export const getClassNameBasedOnAllocatedPercentage = (
  allocatedPercentage: number,
  classToAdd = '',
): string => {
  if (allocatedPercentage > 100) {
    return `over-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 100 && allocatedPercentage > 80) {
    return `mostly-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 80 && allocatedPercentage > 50) {
    return `moderately-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 50 && allocatedPercentage > 30) {
    return `mildly-allocated ${classToAdd}`;
  }

  return `sparsely-allocated ${classToAdd}`;
};

interface OverlappingParam {
  startDate: Moment;
  endDate: Moment;
}
export const isOverlapping = (
  { startDate: startDateA, endDate: endDateA }: OverlappingParam,
  { startDate: startDateB, endDate: endDateB }: OverlappingParam,
): boolean =>
  isDateBetweenRange(startDateA, {
    startDate: startDateB,
    endDate: endDateB,
  }) ||
  isDateBetweenRange(endDateA, {
    startDate: startDateB,
    endDate: endDateB,
  }) ||
  isDateBetweenRange(startDateB, {
    startDate: startDateA,
    endDate: endDateA,
  }) ||
  isDateBetweenRange(endDateB, {
    startDate: startDateA,
    endDate: endDateA,
  });

export const asAllocationPayload = (
  event: AllocationEventModel,
): Allocation => ({
  id: Number(event.id),
  ...(event.get('userId') != null
    ? {
        type: ResourceType.User,
        userId: event.get('userId'),
        ...(event.get('referenceTeamId')
          ? {
              roleId: event.get('roleId'),
              referenceTeamId: event.get('referenceTeamId'),
            }
          : {}),
        overAllocations: event.get('overAllocations'),
      }
    : {
        type: ResourceType.Team,
        teamId: event.get('teamId'),
        roleId: event.get('roleId'),
      }),
  startDate: moment(event.startDate).format(DEFAULT_DATE_FORMAT),
  endDate: moment(event.endDate).format(DEFAULT_DATE_FORMAT),
  minutes: event.get('minutes'),
  totalMinutes: event.get('totalMinutes'),
  projectPhaseId: event.get('projectPhase')?.id,
  billable: event.get('billable') ?? false,
  projectId: event.get('projectId'),
  medium: event.get('medium'),
  sources: event.get('sources'),
  projectFinancialsBudgetId: event.get('projectFinancialsBudgetId'),
  includeSaturday: event.get('includeSaturday') ?? false,
  includeSunday: event.get('includeSunday') ?? false,
  fields: event.get('fields'),
  workId: event.get('workId'),
  workType: event.get('workType'),
});

export const asAllocationForSummary = (
  event:
    | AllocationEvent
    | {
        id: number | string;
        startDate: Date;
        endDate: Date;
        minutes: number;
        totalMinutes: number;
        projectId: number;
        medium: string;
        billable: boolean;
        projectPhase?: { id: number; name: string };
        projectFinancialsBudgetId?: number;
        includeSaturday?: boolean;
        includeSunday?: boolean;
        userId?: number;
        teamId?: number;
        roleId?: number;
        referenceTeamId?: number;
      },
): {
  id: number;
  startDate: string;
  endDate: string;
  minutes: number;
  totalMinutes: number;
  projectId: number;
  userId?: number;
  teamId?: number;
  roleId?: number;
  referenceTeamId?: number;
  medium: string;
  billable: boolean;
  projectPhaseId?: number;
  projectFinancialsBudgetId?: number;
  includeSaturday?: boolean;
  includeSunday?: boolean;
} => ({
  id: Number(event.id),
  startDate: moment(event.startDate).format(DEFAULT_DATE_FORMAT),
  endDate: moment(event.endDate).format(DEFAULT_DATE_FORMAT),
  minutes: event.minutes,
  totalMinutes: event.totalMinutes,
  projectId: event.projectId,
  userId: 'userId' in event ? event.userId : undefined,
  teamId: 'teamId' in event ? event.teamId : undefined,
  roleId: 'roleId' in event ? event.roleId : undefined,
  referenceTeamId:
    'referenceTeamId' in event ? event.referenceTeamId : undefined,
  medium: event.medium,
  billable: event.billable,
  projectPhaseId:
    'projectPhase' in event && event.projectPhase
      ? event.projectPhase.id
      : undefined,
  projectFinancialsBudgetId: event.projectFinancialsBudgetId,
  includeSaturday: event.includeSaturday,
  includeSunday: event.includeSunday,
});

// :TODO Have a separate type for this function
export type NewAllocationParam = Allocation & {
  projectPhase?: {
    id: number;
    name: string;
  };
};

export const idForNewAllocation = (): number => -1 * floatUid();
export const newAllocationEvent = (
  groupBy: GroupBy,
  allocationParam: NewAllocationParam,
  exclusionParams: ExcludeNonWorkingDaysParam,
  softAllocationsEnabled?: boolean,
): AllocationEvent => {
  const startDateMoment = moment(allocationParam.startDate).startOf('day');
  const endDateMoment = moment(allocationParam.endDate).endOf('day');

  const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
    startDateMoment,
    endDateMoment,
    exclusionParams.workingDaysConfig,
    allocationParam.includeSaturday ?? false,
    allocationParam.includeSunday ?? false,
  );
  const commonProps = {
    type: SchedulerEventType.Allocation as const,
    id: allocationParam.id,
    startDate: startDateMoment.toDate(),
    endDate: endDateMoment.toDate(),
    minutes: allocationParam.minutes,
    totalMinutes: numberOfWorkingDays * allocationParam.minutes,
    projectPhase: allocationParam.projectPhase,
    billable: allocationParam.billable,
    projectId: allocationParam.projectId,
    overAllocations: [],
    overAllocationsForUI: [],
    projectFinancialsBudgetId: allocationParam.projectFinancialsBudgetId,
    medium: allocationParam.medium,
    includeSaturday: allocationParam.includeSaturday ?? false,
    includeSunday: allocationParam.includeSunday ?? false,
    previewText: normalizeAllocationPreviewHtml(allocationParam.previewText),
    ...getSchedulerEventProps(SchedulerEventType.Allocation, {
      cls: getSchedulerEventsClass(
        SchedulerEventType.Allocation,
        `${softAllocationsEnabled ? allocationParam.medium : ''}`.toLowerCase(),
      ),
    }),
  };

  if (allocationParam.type === ResourceType.User) {
    return {
      ...commonProps,
      resourceId:
        groupBy === GroupBy.Projects
          ? resourceIdForUserAsLeaf({
              projectId: allocationParam.projectId,
              userId: allocationParam.userId,
              roleId: allocationParam.roleId,
              referenceTeamId: allocationParam.referenceTeamId,
              budgetId: allocationParam.projectFinancialsBudgetId,
            })
          : resourceIdForProjectAsLeaf({
              projectId: allocationParam.projectId,
              userId: allocationParam.userId,
              roleId: allocationParam.roleId,
              referenceTeamId: allocationParam.referenceTeamId,
              budgetId: allocationParam.projectFinancialsBudgetId,
            }),
      userId: allocationParam.userId,
      roleId: allocationParam.roleId,
      referenceTeamId: allocationParam.referenceTeamId,
    };
  }

  return {
    ...commonProps,
    resourceId: resourceIdForTeamAsLeaf(
      allocationParam.projectId,
      allocationParam.teamId,
      allocationParam.projectFinancialsBudgetId,
    ),
    teamId: allocationParam.teamId,
    roleId: allocationParam.roleId,
    projectFinancialsBudgetId: allocationParam.projectFinancialsBudgetId,
  };
};
export const asValidAllocationObject = (data: any): Allocation => {
  if (data.userId != null) {
    return {
      ...data,
      type: ResourceType.User,
    };
  }

  return {
    ...data,
    type: ResourceType.Team,
  };
};
